// The small marks of the map: a wreck, a point of interest and a pirate
// den (R2851, ui-map-interface.md §8 "The small marks").
//
// Each small mark takes a press and a hover of its own. The hit area is
// the drawn box of the mark; a finger adds `touch_margin_px` of
// `[chart.piece.wrecks.fig]` on every side, in screen px, and never the
// finger box of a hull, which at close zoom covers most of a cell. Where
// two boxes hold the point, the mark whose anchor lies nearer wins.
//
// A pick is one scan of the marks each kind already packed (§10 rule 2):
// the wrecks piece and the points of interest piece offer their drawn
// marks into ONE reused `SmallMark`, and the den rows of the Empire frame
// follow. Nothing here allocates.

import type { DenInfo, PointOfInterest, Wreck } from '../gen/proto/aquaphobia';

/** The three kinds of small mark. */
export type SmallMarkKind = 'wreck' | 'poi' | 'den';

/** The small mark a pick found. One object is reused for every pick. */
export interface SmallMark {
  /** The kind, or null while no mark holds the point. */
  kind: SmallMarkKind | null;
  /** The anchor of the mark, in world px. */
  x: number;
  y: number;
  /** The squared distance from the point to the anchor. */
  d2: number;
  wreck: Wreck | null;
  poi: PointOfInterest | null;
  den: DenInfo | null;
}

/** A fresh empty mark. */
export function newSmallMark(): SmallMark {
  return { kind: null, x: 0, y: 0, d2: Infinity, wreck: null, poi: null, den: null };
}

/** Empties a mark before a pick. */
export function clearSmallMark(m: SmallMark): void {
  m.kind = null;
  m.d2 = Infinity;
  m.wreck = null;
  m.poi = null;
  m.den = null;
}

/**
 * Does the box `x0..x1, y0..y1` round the anchor `ax, ay`, grown by
 * `margin` on every side, hold the point `px, py`? All in world px.
 */
export function boxHolds(
  px: number, py: number, ax: number, ay: number,
  x0: number, y0: number, x1: number, y1: number, margin: number,
): boolean {
  const dx = px - ax, dy = py - ay;
  return dx >= x0 - margin && dx <= x1 + margin && dy >= y0 - margin && dy <= y1 + margin;
}

/**
 * Offers a mark whose box holds the point: it takes `m` when its anchor
 * lies nearer to the point than the mark `m` holds. Answers true when it
 * took it, so the caller writes the reference.
 */
export function offerSmallMark(
  m: SmallMark, kind: SmallMarkKind, ax: number, ay: number, px: number, py: number,
): boolean {
  const d2 = (px - ax) * (px - ax) + (py - ay) * (py - ay);
  if (d2 >= m.d2) return false;
  m.kind = kind;
  m.x = ax;
  m.y = ay;
  m.d2 = d2;
  m.wreck = null;
  m.poi = null;
  m.den = null;
  return true;
}
